import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const FORM_SUBMIT_URL = 'http://ipv6c.com/';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypotVal = (fd.get('website_alt') as string || '').trim();
    if (honeypotVal !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    fd.delete('website_alt');
    setFormStatus('loading');
    setFormError('');
    try {
      const params = new URLSearchParams();
      fd.forEach((value, key) => { params.append(key, value.toString()); });
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
    setTimeout(() => setFormStatus('idle'), 3000);
  };

  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫画网',
    url: 'http://ipv6c.com/',
    description: '韩漫画网设有免费漫画弹窗入口，作为海量正版漫画在线阅读平台，内容安全可靠，让您安心享受高质量漫画资源。',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@ipv6c.com',
      availableLanguage: ['Chinese'],
    },
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ipv6c.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://ipv6c.com/about' },
    ],
  };

  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于我们 - 韩漫画网',
    description: '了解韩漫画网品牌故事、平台数据、读者口碑与商务合作联系方式。海量正版漫画在线阅读平台，安全可靠，每日更新。',
    url: 'http://ipv6c.com/about',
    isPartOf: { '@type': 'WebSite', name: '韩漫画网', url: 'http://ipv6c.com/' },
  };

  const trustBadges = [
    { icon: 'ri-shield-check-line', title: '正版授权', desc: '所有漫画内容均获得正版授权，确保内容合法合规' },
    { icon: 'ri-refresh-line', title: '每日更新', desc: '热门作品每日更新，让您第一时间阅读最新章节' },
    { icon: 'ri-lock-line', title: '安全可靠', desc: '严格的内容审核机制，为您提供安全舒适的阅读环境' },
  ];

  const platformStats = [
    { value: '10,000+', label: '正版漫画作品', icon: 'ri-book-open-line' },
    { value: '500万+', label: '月活跃读者', icon: 'ri-user-smile-line' },
    { value: '99.9%', label: '服务可用率', icon: 'ri-server-line' },
    { value: '8大', label: '漫画分类', icon: 'ri-grid-line' },
    { value: '50+', label: '合作出版社', icon: 'ri-building-4-line' },
    { value: '24h', label: '内容审核响应', icon: 'ri-timer-line' },
  ];

  const testimonials = [
    { name: '张先生', role: '漫画爱好者', text: '韩漫画网的资源太全了，很多小众韩漫都能找到，画质清晰，阅读流畅，推荐给所有喜欢韩漫的朋友！' },
    { name: '李女士', role: '大学生', text: '作为韩漫忠粉，这个平台真的满足了我所有需求。免费弹窗入口很方便，漫画更新速度也很快。' },
    { name: '王同学', role: '自由职业者', text: '用了半年多了，界面简洁美观，分类清晰，找漫画特别方便。正版内容看着就是舒服。' },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdOrg) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }}
      />
      <Navbar />

      <main className="pt-16">
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="lg:w-1/2">
                <span className="text-xs text-accent-600 font-medium tracking-wider uppercase">
                  品牌故事
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mt-2 mb-5 leading-tight">
                  韩漫画网 —
                  <span className="text-primary-500">海量正版</span>漫画
                  <br />在线阅读平台
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  韩漫画网成立于2023年，致力于为全球中文读者提供高品质的韩国漫画阅读体验。
                  作为海量正版漫画在线阅读平台，我们与多家韩国顶级漫画出版社建立了深度合作关系，
                  确保每一部作品都经过正版授权，内容安全可靠。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  平台设有免费漫画弹窗入口，让读者可以便捷地进入漫画世界。
                  无论是热门的恋爱漫画、热血的奇幻冒险，还是扣人心弦的悬疑剧情，
                  韩漫画网都能满足您的阅读需求。我们坚持每日更新，让您不错过任何精彩内容。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  韩漫画网的使命是让每一位漫画爱好者都能安心享受高质量的漫画资源，
                  我们相信，好的漫画作品值得被更多人看见。
                </p>
              </div>
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20interior%20with%20warm%20coral%20and%20cream%20tones%2C%20digital%20illustration%20tablets%20displaying%20colorful%20webtoon%20art%2C%20bookshelves%20filled%20with%20manhwa%20volumes%2C%20cozy%20creative%20studio%20atmosphere%2C%20soft%20natural%20light%20from%20large%20windows%2C%20artistic%20vibes%2C%20clean%20modern%20aesthetic%2C%20professional%20yet%20creative%20environment&width=800&height=600&seq=about-brand-visual&orientation=landscape"
                  alt="韩漫画网品牌 - 海量正版漫画在线阅读平台"
                  className="w-full rounded-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
                平台<span className="text-accent-500">数据</span>
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
                用数据说话，韩漫画网持续为读者创造价值
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 max-w-5xl mx-auto">
              {platformStats.map((stat) => (
                <div
                  key={stat.label}
                  className="p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center transition-all duration-300 hover:-translate-y-1"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mx-auto mb-3">
                    <i className={`${stat.icon} text-lg`}></i>
                  </span>
                  <div className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
                我们的<span className="text-secondary-500">承诺</span>
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
                三大核心保障，让您安心阅读
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-4xl mx-auto">
              {trustBadges.map((badge) => (
                <div
                  key={badge.title}
                  className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70 text-center"
                >
                  <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className={`${badge.icon} text-2xl`}></i>
                  </span>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">
                    {badge.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {badge.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
                读者<span className="text-primary-500">口碑</span>
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
                听听韩漫画网的读者们怎么说
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="p-6 rounded-2xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className="w-4 h-4 flex items-center justify-center text-secondary-500">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                    <span className="text-xs text-foreground-500">{t.role}</span>
                    <span className="w-4 h-4 flex items-center justify-center text-accent-500 ml-auto">
                      <i className="ri-verified-badge-fill"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <span className="text-xs text-accent-600 font-medium tracking-wider uppercase">
                商务合作
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mt-2">
                合作<span className="text-accent-500">联系</span>我们
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
                无论您是出版社、广告商还是内容创作者，欢迎与我们洽谈合作
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 max-w-5xl mx-auto">
              <div className="lg:w-2/5">
                <div className="grid grid-cols-1 gap-4">
                  <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">
                      电子邮箱
                    </h3>
                    <p className="text-sm text-foreground-600">hello@ipv6c.com</p>
                  </div>

                  <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-wechat-line text-lg"></i>
                    </span>
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">
                      微信公众号
                    </h3>
                    <p className="text-sm text-foreground-600">韩漫画网官方</p>
                  </div>

                  <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-3">
                      <i className="ri-customer-service-2-line text-lg"></i>
                    </span>
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">
                      在线客服
                    </h3>
                    <p className="text-sm text-foreground-600">工作日 9:00-18:00</p>
                  </div>

                  <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-team-line text-lg"></i>
                    </span>
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">
                      合作方向
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      内容授权合作 · 广告投放合作 · 品牌联名推广 · IP 授权开发
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:w-3/5">
                <div className="p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-1">
                    提交合作意向
                  </h3>
                  <p className="text-sm text-foreground-500 mb-6">
                    请填写以下信息，我们会在 2 个工作日内与您联系
                  </p>

                  <form
                    id="business-cooperation-form"
                   
                    onSubmit={handleFormSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div>
                      <label htmlFor="bc-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        您的姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="bc-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        placeholder="请输入您的姓名"
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="bc-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="bc-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                        placeholder="请输入您的邮箱地址"
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="bc-company" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        公司/机构名称
                      </label>
                      <input
                        id="bc-company"
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleFormChange}
                        placeholder="请输入您所在的公司或机构名称"
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="bc-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        合作意向说明 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="bc-message"
                        name="message"
                        value={formData.message}
                        onChange={handleFormChange}
                        required
                        maxLength={500}
                        rows={4}
                        placeholder="请简要描述您的合作意向与需求（500字以内）"
                        className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">
                        {formData.message.length}/500
                      </p>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                    >
                      {formStatus === 'loading' ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 flex items-center justify-center animate-spin">
                            <i className="ri-loader-4-line"></i>
                          </span>
                          提交中...
                        </span>
                      ) : (
                        '提交合作意向'
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center bg-accent-100/50 py-2.5 rounded-xl">
                        提交成功！我们会在 2 个工作日内与您联系，感谢您的关注。
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-primary-600 text-center bg-primary-100/50 py-2.5 rounded-xl">
                        {formError}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}